import React, { useState, useEffect } from 'react';
import { Modal, Button, Input } from '@/components/ui';
import type { MaintenanceSchedule, ScheduleStatus, PriorityCode } from '../types/maintenance-schedules.types';
import { useSchedules } from '../hooks/useSchedules';
import { toast } from 'sonner';

interface ScheduleFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  item?: MaintenanceSchedule | null;
  companyId: string;
}

const statusOptions: { value: ScheduleStatus; label: string }[] = [
  { value: 'draft', label: 'Bản nháp' },
  { value: 'assigned', label: 'Đã giao việc' },
  { value: 'in_progress', label: 'Đang thực hiện' },
  { value: 'completed', label: 'Đã hoàn thành' },
  { value: 'delayed', label: 'Bị trễ' },
  { value: 'cancelled', label: 'Đã hủy' },
  { value: 'missed', label: 'Bỏ lỡ' },
];

const priorityOptions: { value: PriorityCode; label: string }[] = [
  { value: 'low', label: 'Thấp' },
  { value: 'medium', label: 'Trung bình' },
  { value: 'high', label: 'Cao' },
  { value: 'critical', label: 'Khẩn cấp' },
];

export function ScheduleFormModal({ isOpen, onClose, item, companyId }: ScheduleFormModalProps) {
  const isEdit = !!item;
  const { createSchedule, updateSchedule, isCreating, isUpdating } = useSchedules(companyId);

  const [formData, setFormData] = useState<Partial<MaintenanceSchedule>>({
    code: '',
    status_code: 'draft',
    priority_code: 'medium',
    scheduled_date: '',
    start_time: '',
    end_time: '',
    estimated_hours: 0,
    project_id: '',
    assigned_team_id: '',
    assigned_to: '',
    notes: '',
  });

  useEffect(() => {
    if (isOpen) {
      if (item) {
        setFormData(item);
      } else {
        setFormData({
          code: `SCH-${Math.random().toString(36).substr(2, 6).toUpperCase()}`,
          status_code: 'draft',
          priority_code: 'medium',
          scheduled_date: new Date().toISOString().split('T')[0],
          start_time: '08:00',
          end_time: '17:00',
          estimated_hours: 0,
          project_id: '',
          assigned_team_id: '',
          assigned_to: '',
          notes: '',
          company_id: companyId,
        });
      }
    }
  }, [isOpen, item, companyId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (isEdit && item) {
        await updateSchedule({ id: item.id, updates: formData });
      } else {
        await createSchedule({ ...formData, company_id: companyId });
      }
      onClose();
      toast.success(isEdit ? 'Cập nhật lịch trình thành công' : 'Lập lịch trình mới thành công');
    } catch (error: any) {
      toast.error('Lỗi: ' + error.message);
    }
  };

  const isPending = isCreating || isUpdating;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Cập nhật Lịch Bảo dưỡng' : 'Lập Lịch Bảo dưỡng mới'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-6 py-2">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Mã lịch trình *"
            required
            value={formData.code || ''}
            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
            placeholder="VD: SCH-123456"
          />
          <Input
            label="ID Dự án *"
            required
            value={formData.project_id || ''}
            onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
            placeholder="ID dự án liên kết..."
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            label="Ngày thực hiện *"
            type="date"
            required
            value={formData.scheduled_date || ''}
            onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
          />
          <Input
            label="Bắt đầu"
            type="time"
            value={formData.start_time || ''}
            onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
          />
          <Input
            label="Kết thúc"
            type="time"
            value={formData.end_time || ''}
            onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Mức độ ưu tiên *</label>
            <select
              required
              className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-2.5 transition-all"
              value={formData.priority_code || ''}
              onChange={(e) => setFormData({ ...formData, priority_code: e.target.value as PriorityCode })}
            >
              {priorityOptions.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Trạng thái *</label>
            <select
              required
              className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-2.5 transition-all"
              value={formData.status_code || ''}
              onChange={(e) => setFormData({ ...formData, status_code: e.target.value as ScheduleStatus })}
            >
              {statusOptions.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="ID Tổ đội (Team)"
            value={formData.assigned_team_id || ''}
            onChange={(e) => setFormData({ ...formData, assigned_team_id: e.target.value })}
          />
          <Input
            label="ID Phụ trách (Assignee)"
            value={formData.assigned_to || ''}
            onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">Ghi chú lịch trình</label>
          <textarea
            className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-3 min-h-[80px]"
            placeholder="Các lưu ý đặc biệt cho tổ đội khi thực hiện lịch trình này..."
            value={formData.notes || ''}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          />
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-slate-50">
          <Button variant="outline" type="button" onClick={onClose} disabled={isPending}>Hủy</Button>
          <Button variant="primary" type="submit" isLoading={isPending}>
            {isEdit ? 'Lưu thay đổi' : 'Lập lịch trình'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
